import { formatMoney } from './helpers'
import { SellingPlanInterval } from './plan-models'

export type WorkflowTriggerType =
  | 'subscription-activated'
  | 'subscription-paused'
  | 'subscription-resumed'
  | 'subscription-cancelled'
  | 'subscription-expired'
  | 'subscription-payment-failed-retrying'
  | 'subscription-payment-failed-last-attempt'
  | 'subscription-payment-succeeded'
  | 'subscription-secondary-price-adjustment-applied'

export const workflowTriggerLabels: Record<WorkflowTriggerType, string> = {
  'subscription-activated': 'Subscription is created',
  'subscription-paused': 'Subscription is paused',
  'subscription-resumed': 'Subscription is resumed',
  'subscription-cancelled': 'Subscription is cancelled',
  'subscription-expired': 'Subscription expires',
  'subscription-payment-failed-retrying': 'Subscription payment fails (retrying)',
  'subscription-payment-failed-last-attempt': 'Subscription payment fails (last attempt)',
  'subscription-payment-succeeded': 'Subscription payment succeeds',
  'subscription-secondary-price-adjustment-applied': 'Future subscription price change applied',
}

export const workflowTriggerTypes = Object.keys(workflowTriggerLabels) as WorkflowTriggerType[]

export const workflowTriggersSupportOrderTags = (triggers: WorkflowTriggerType[]): boolean =>
  triggers.includes('subscription-activated') ||
  triggers.includes('subscription-payment-succeeded') ||
  triggers.includes('subscription-secondary-price-adjustment-applied')

export type WorkflowFilterType =
  | 'subscription-contains-any-of-selected-selling-plans'
  | 'subscription-contains-any-of-selected-selling-plan-groups'
  | 'subscription-contains-any-of-selected-products'
  | 'subscription-does-not-contain-any-of-selected-products'
  | 'subscription-contains-all-selected-products'
  | 'subscription-contains-any-of-selected-product-variants'
  | 'subscription-does-not-contain-any-of-selected-product-variants'
  | 'subscription-contains-all-selected-product-variants'
  | 'subscription-contains-any-matching-product-title'
  | 'subscription-contains-any-matching-product-variant-title'
  | 'customer-has-no-active-subscriptions'
  | 'after-nth-order'
  | 'product-has-no-secondary-price-adjustment'
  | 'product-variant-has-no-secondary-price-adjustment'

export const workflowFilterLabels: Record<WorkflowFilterType, string> = {
  'subscription-contains-any-of-selected-selling-plans':
    'Subscription contains ANY of the selected selling plans',
  'subscription-contains-any-of-selected-selling-plan-groups':
    'Subscription contains ANY of the selected selling plan groups',
  'subscription-contains-any-of-selected-products':
    'Subscription contains ANY of the selected products',
  'subscription-does-not-contain-any-of-selected-products':
    'Subscription DOES NOT contain ANY of the selected products',
  'subscription-contains-all-selected-products':
    'Subscription contains ALL of the selected products',
  'subscription-contains-any-of-selected-product-variants':
    'Subscription contains ANY of the selected product variants',
  'subscription-does-not-contain-any-of-selected-product-variants':
    'Subscription DOES NOT contain ANY of the selected product variants',
  'subscription-contains-all-selected-product-variants':
    'Subscription contains ALL of the selected product variants',
  'subscription-contains-any-matching-product-title':
    'Subscription contains ANY product with matching title',
  'subscription-contains-any-matching-product-variant-title':
    'Subscription contains ANY product variant with matching title',
  'customer-has-no-active-subscriptions': 'Customer has no active subscriptions',
  'after-nth-order': 'Total orders count',
  'product-has-no-secondary-price-adjustment': 'Selected product HAS NO future price change',
  'product-variant-has-no-secondary-price-adjustment':
    'Selected product variant HAS NO future price change',
}

export const workflowFilterTypes = Object.keys(workflowFilterLabels) as WorkflowFilterType[]

export type WorkflowRootFilterType = 'and' | 'or'

export type WorkflowEmptyDataType = []
export type WorkflowTagsDataType = string[]
export type WorkflowProductDataType = [] | [string, string]

/**
 * Price configured on a workflow action (swap product, add product, fixed amount discount, delivery price):
 * - decimal string: legacy format, price in the shop currency. A zero price applies to any currency
 * - record: currency code => decimal string,
 *   with an optional WORKFLOW_ACTION_ANY_CURRENCY entry used for every currency that is not listed
 * - null: keep the current price
 */
export type WorkflowActionPriceType = string | Record<string, string> | null
export type WorkflowActionRequiredPriceType = Exclude<WorkflowActionPriceType, null>

/** Key of the price applied regardless of the subscription currency. Not an ISO 4217 code. */
export const WORKFLOW_ACTION_ANY_CURRENCY = 'ANY'

export const isWorkflowActionAnyCurrency = (code: string): boolean =>
  code === WORKFLOW_ACTION_ANY_CURRENCY

export const isLegacyWorkflowActionPrice = (price: WorkflowActionPriceType): price is string =>
  typeof price === 'string'

export const normalizeWorkflowActionPrice = (
  price: WorkflowActionRequiredPriceType,
  shopCurrency: string
): Record<string, string> => {
  if (!isLegacyWorkflowActionPrice(price)) {
    return price
  }

  // A legacy zero price has always been applied regardless of the subscription currency
  return { [parseFloat(price) === 0 ? WORKFLOW_ACTION_ANY_CURRENCY : shopCurrency]: price }
}

export const formatWorkflowActionPrice = (
  price: WorkflowActionPriceType | undefined,
  shopCurrency: string
): string | null => {
  if (!price) {
    return null
  }

  if (isLegacyWorkflowActionPrice(price)) {
    return formatMoney(parseFloat(price) * 100, shopCurrency)
  }

  const isSet = (amount: string | null | undefined): amount is string =>
    amount !== undefined && amount !== null && amount !== ''

  const parts = Object.entries(price)
    .filter(([code, amount]) => !isWorkflowActionAnyCurrency(code) && isSet(amount))
    .map(([code, amount]) => formatMoney(parseFloat(amount) * 100, code))

  // The "any currency" price is a fallback for the listed currencies, so it goes last
  const anyCurrencyAmount: string | undefined = price[WORKFLOW_ACTION_ANY_CURRENCY]

  if (isSet(anyCurrencyAmount)) {
    parts.push(
      `${parseFloat(anyCurrencyAmount).toFixed(2)} in any ${
        parts.length > 0 ? 'other ' : ''
      }currency`
    )
  }

  return parts.length > 0 ? parts.join(' / ') : null
}

export type WorkflowSwapProductDataType =
  | [
      WorkflowProductDataType, // sourceProduct
      WorkflowProductDataType, // targetProduct
      number | null, // quantity
      WorkflowActionPriceType // currentPrice
    ]
  | []
export type WorkflowAddProductDataType =
  | [
      WorkflowProductDataType,
      number, // quantity
      WorkflowActionRequiredPriceType, // currentPrice
      number | null // recurringCycleLimit
    ]
  | []
export type WorkflowAddDiscountDataType = [
  string, // discountCodeName
  string | null, // recurringCycleLimit
  'percentage' | 'fixedAmount', // discountType
  WorkflowActionRequiredPriceType, // discountPercentage (always a string) or discountFixedAmount
  number? // appliesOnEachItem
]

export type WorkflowEditDeliveryFrequencyDataType = [
  number, // interval frequency
  SellingPlanInterval, // interval type
  number // pre-paid deliveries (1 for non pre-paid subscriptions)
]

export type WorkflowEditDeliveryMethodDataType = [
  string | null, // deliveryMethodTitle
  string | null, // deliveryMethodCode
  WorkflowActionPriceType // deliveryPrice
]

export type WorkflowActionAddCustomerTagsType = ['add-customer-tags', WorkflowTagsDataType]
export type WorkflowActionRemoveCustomerTagsType = ['remove-customer-tags', WorkflowTagsDataType]
export type WorkflowActionAddOrderTagsType = ['add-order-tags', WorkflowTagsDataType]
export type WorkflowActionRemoveOrderTagsType = ['remove-order-tags', WorkflowTagsDataType]
export type WorkflowActionAddOneTimeGiftProductType = [
  'add-one-time-gift-product',
  WorkflowProductDataType
]
export type WorkflowActionAddProductType = ['add-product', WorkflowAddProductDataType]
export type WorkflowActionRemoveProductType = ['remove-product', WorkflowProductDataType]
export type WorkflowActionRemoveProductVariantType = [
  'remove-product-variant',
  WorkflowProductDataType
]
export type WorkflowActionSwapProductType = ['swap-product', WorkflowSwapProductDataType]
export type WorkflowActionAddDiscountType = ['add-discount', WorkflowAddDiscountDataType]
export type WorkflowActionRemoveOneTimeGiftProductType = [
  'remove-one-time-gift-products',
  WorkflowEmptyDataType
]
export type WorkflowActionRemoveExpiredDiscountType = [
  'remove-expired-discount',
  WorkflowEmptyDataType
]

export type WorkflowActionEditDeliveryFrequencyType = [
  'edit-delivery-frequency',
  WorkflowEditDeliveryFrequencyDataType
]

export type WorkflowActionEditDeliveryMethodType = [
  'edit-delivery-method',
  WorkflowEditDeliveryMethodDataType
]

export type WorkflowAdjustLinePriceForPricingPolicyItem = [
  string, // lineId
  string, // productName
  string // newPrice
]

export type WorkflowAdjustLinePriceForPricingPolicyDataType =
  WorkflowAdjustLinePriceForPricingPolicyItem[]

export type WorkflowActionAdjustLinePriceForPricingPolicyType = [
  'adjust-line-price-for-pricing-policy',
  WorkflowAdjustLinePriceForPricingPolicyDataType
]

export type WorkflowAction =
  | WorkflowActionAddCustomerTagsType
  | WorkflowActionRemoveCustomerTagsType
  | WorkflowActionAddOrderTagsType
  | WorkflowActionRemoveOrderTagsType
  | WorkflowActionAddOneTimeGiftProductType
  | WorkflowActionAddProductType
  | WorkflowActionSwapProductType
  | WorkflowActionRemoveProductType
  | WorkflowActionRemoveProductVariantType
  | WorkflowActionAddDiscountType
  | WorkflowActionRemoveOneTimeGiftProductType
  | WorkflowActionRemoveExpiredDiscountType
  | WorkflowActionEditDeliveryFrequencyType
  | WorkflowActionEditDeliveryMethodType
  | WorkflowActionAdjustLinePriceForPricingPolicyType

type WorkflowActionTypeMapping<T extends WorkflowAction> = T[0]
type WorkflowActionDataMapping<T extends WorkflowAction> = T[1]
export type WorkflowActionType = WorkflowActionTypeMapping<WorkflowAction>
export type WorkflowActionTagType = Extract<
  WorkflowActionType,
  'add-customer-tags' | 'remove-customer-tags' | 'add-order-tags' | 'remove-order-tags'
>

export const workflowInternalActionTypes: WorkflowActionType[] = [
  'remove-expired-discount',
  'adjust-line-price-for-pricing-policy',
  'remove-one-time-gift-products',
]

export type WorkflowInternalActionType = Extract<
  WorkflowActionType,
  (typeof workflowInternalActionTypes)[number]
>

export type WorkflowActionDataType = WorkflowActionDataMapping<WorkflowAction>

export const workflowActionLabels: Record<WorkflowActionType, string> = {
  'add-customer-tags': 'Add tags to the customer',
  'remove-customer-tags': 'Remove tags from the customer',
  'add-order-tags': 'Add tags to the order',
  'remove-order-tags': 'Remove tags from the order',
  'add-one-time-gift-product': 'Add one-time free gift product',
  'add-product': 'Add product',
  'swap-product': 'Swap product',
  'remove-product': 'Remove product',
  'remove-product-variant': 'Remove product variant',
  'add-discount': 'Add discount',
  'remove-one-time-gift-products': 'Remove one-time products',
  'remove-expired-discount': 'Remove expired discount',
  'edit-delivery-frequency': 'Edit delivery frequency',
  'edit-delivery-method': 'Edit delivery method',
  'adjust-line-price-for-pricing-policy': 'Adjust product price',
}

export const workflowActionTypes = Object.keys(workflowActionLabels) as WorkflowActionType[]

export const actionIsSupportedByTriggers = (
  actionType: WorkflowActionType,
  triggers: WorkflowTriggerType[]
): boolean =>
  (actionType !== 'add-order-tags' && actionType !== 'remove-order-tags') ||
  (['add-order-tags', 'remove-order-tags'].includes(actionType) &&
    workflowTriggersSupportOrderTags(triggers))

export type WorkflowTemplateID = 'tag-active-subscribers'

export const workflowTemplateLabels: Record<WorkflowTemplateID, string> = {
  'tag-active-subscribers': 'Tag active subscribers',
}

export const workflowTemplateIDs = Object.keys(workflowTemplateLabels) as WorkflowTemplateID[]

export type WorkflowFilter = [WorkflowFilterType, any[]]
export type WorkflowRootFilter = [WorkflowRootFilterType, WorkflowFilter[]]

export interface Workflow {
  id: number
  name: string | null
  is_active: boolean
  triggers: WorkflowTriggerType[]
  filter: WorkflowRootFilter
  actions: WorkflowAction[]
  selling_plan_id: string | null
}
